import React, { useState, useRef, useEffect } from "react";
import { FaBookmark, FaRegBookmark } from "react-icons/fa";
import QRCodeRenderer from "../shared/QRCodeRenderer";
import Button from "../shared/Button";
import DownloadIcon from "../../assets/icons/DownloadIcon";
import AnimatedQRCode from "../utils/AnimatedQRCode";
import EditURLEntryModal from "./EditURLEntryModal";
import StatisticsModal from "../shared/StatisticsModal";
import { useURLEntry } from "../../hooks/useURLEntry";
import { useLogos } from "../../hooks/useLogos";
import { useUserId } from "../../hooks/useUserId";
import { useStatisticsModal } from "../../hooks/useStatisticsModal";
import { PREFIX_OPTIONS } from "../../constants/prefixes";
import { QR_CONFIG } from "../../constants/qrCodeConstants";
import "../../styles/URLEntry.css";
import CardHeader, { MenuItem } from "../shared/cards/CardHeader";
import BaseCard from "../shared/cards/BaseCard";
import CardContent from "../shared/cards/CardContent";

interface URLEntryProps {
  shortCode: string;
  url: string;
  name: string;
  expirationDate: string;
  fileFormat: "png" | "svg";
  prefix: string;
  isFavourite: boolean;
  customColor?: string;
  withLogo?: boolean;
  logoId?: string;
  showHeader?: boolean;
  useAnimatedQRCode?: boolean;
  defaultExpanded?: boolean;
  totalScans?: number;
  onDelete: (shortCode: string) => void;
  onCopy: (shortUrl: string) => void;
  onFileFormatChange: (shortCode: string, format: "png" | "svg") => void;
  onDownloadQRCode: (shortCode: string, format?: "png" | "svg") => void;
  onRedirect: (shortCode: string) => void;
  onToggleFavourite: (shortCode: string) => void;
  onUpdate: (
    shortCode: string,
    newUrl: string,
    newName: string,
    oldUrl: string,
    oldName: string,
    newExpirationDate: string | null,
    customColor: string,
    withLogo: boolean,
    newPrefix: string,
    selectedLogoId?: string
  ) => void;
}

const URLEntry: React.FC<URLEntryProps> = ({
  shortCode,
  url,
  name,
  expirationDate,
  isFavourite,
  showHeader = true,
  onDelete,
  onCopy,
  onFileFormatChange,
  onDownloadQRCode,
  onToggleFavourite,
  onUpdate,
  useAnimatedQRCode = false,
  defaultExpanded = false,
  customColor: customColorProp = QR_CONFIG.STYLE.DEFAULT_COLOR,
  withLogo,
  prefix,
  logoId,
  totalScans = 0,
}) => {
  const userId = useUserId();
  const { allLogos, deleteLogo, uploadLogo, validateLogoFile, validateLogoName } = useLogos(userId);

  const {
    qrOptions,
    setQrOptions,
    customColor,
    setCustomColor,
    editedName,
    setEditedName,
    editedUrl,
    setEditedUrl,
    editedExpirationDate,
    setEditedExpirationDate,
    editVisible,
    openEditModal,
    closeEditModal,
    initialQrOptions,
    initialCustomColor,
    editedPrefix,
    setEditedPrefix,
    initialPrefix,
  } = useURLEntry({
    name,
    url,
    expirationDate,
    defaultExpanded,
    customColor: customColorProp,
    withLogo,
    prefix,
    logoId,
  });

  const { isOpen: isStatisticsOpen, openStatistics, closeStatistics } = useStatisticsModal();

  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const downloadRef = useRef<HTMLDivElement>(null);

  const backendUrlRaw = process.env.REACT_APP_BACKEND_URL || "http://localhost:5000";
  // If REACT_APP_BACKEND_URL is an absolute URL (local dev: "http://localhost:5000"), use it directly.
  // If it's a relative path ("/api") or unset (production SWA), prepend window.location.origin
  // so the QR code value is always a fully absolute, scannable URL.
  const backendBase = backendUrlRaw.startsWith("http")
    ? backendUrlRaw.replace(/\/$/, "")
    : `${window.location.origin}${backendUrlRaw}`;
  const shortUrl = `${backendBase}/go/${shortCode}`;

  useEffect(() => {
    if (!downloadMenuOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target as Node)) {
        setDownloadMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [downloadMenuOpen]);

  const getLogoUrl = (id?: string | null): string => {
    if (!id) return "/images/CGI-Logo.jpg";
    const foundLogo = allLogos.find((logo) => logo.id === id);
    if (foundLogo) return foundLogo.logoUrl;

    const logoMappings: Record<string, string> = {
      cgi: "/images/CGI-Logo.jpg",
      ais: "/images/AIS-Logo.svg",
    };
    return logoMappings[id] || logoMappings["cgi"];
  };

  const handleDownloadSelect = (format: "png" | "svg") => {
    onFileFormatChange(shortCode, format);
    onDownloadQRCode(shortCode, format);
    setDownloadMenuOpen(false);
  };

  const handleLogoDelete = async (logoIdToDelete: string): Promise<boolean> => {
    const logo = allLogos.find((l) => l.id === logoIdToDelete);
    return deleteLogo(logoIdToDelete, logo?.name || "Logo");
  };

  const formatUrl = (u: string) => u.replace(/^https?:\/\/(api\.)?/, "");
  const formatExpirationDate = (d: string) => (d ? new Date(d).toLocaleDateString("en-CA") : "not set");

  const currentLogoId = qrOptions.selectedLogoId ?? logoId;
  const logoUrl = getLogoUrl(currentLogoId);
  const shouldShowLogo = qrOptions.withLogo;

  const menuItems: MenuItem[] = [
    { label: "Edit", onClick: openEditModal },
    { label: "Delete", onClick: () => onDelete(shortCode), variant: "danger" },
  ];

  const renderQRCode = () => {
    if (useAnimatedQRCode) {
      return (
        <AnimatedQRCode
          value={shortUrl}
          size={QR_CONFIG.DISPLAY_SIZE}
          logoSrc={shouldShowLogo ? logoUrl : undefined}
          logoWidth={QR_CONFIG.LOGO.SIZE}
          logoHeight={QR_CONFIG.LOGO.SIZE}
          excavate={shouldShowLogo}
          color={customColor}
        />
      );
    }

    return (
      <QRCodeRenderer
        value={shortUrl}
        size={QR_CONFIG.DISPLAY_SIZE}
        color={customColor}
        withLogo={shouldShowLogo}
        logoUrl={shouldShowLogo ? logoUrl : undefined}
      />
    );
  };

  const renderFavouriteButton = () => (
    <button
      className="url-card-fav-btn"
      onClick={(e) => {
        e.stopPropagation();
        onToggleFavourite(shortCode);
      }}
      aria-label="Toggle favourite"
    >
      {isFavourite ? (
        <FaBookmark size={24} color="#5236ab" />
      ) : (
        <FaRegBookmark size={24} color="#AAA" />
      )}
    </button>
  );

  const renderInfoSection = () => (
    <>
      <div className="card-info-lines url-card-lines">
        <div className="url-card-url-line">
          <span className="card-label">Original URL:</span>
          <span className="card-value" title={url}>{formatUrl(url)}</span>
        </div>
        <div>
          <span className="card-label">Short URL:</span>
          <a className="card-link" href={shortUrl} target="_blank" rel="noopener noreferrer">
            {formatUrl(shortUrl)}
          </a>
        </div>
        <div>
          <span className="card-label">Expires:</span>
          <span className="card-value">{formatExpirationDate(expirationDate)}</span>
        </div>
        <div className="url-card-stats-line">
          <span className="card-label">Statistics:</span>
          {totalScans === 0 ? (
            <span className="url-card-stats-empty">Not yet viewed</span>
          ) : (
            <>
              <span className="card-value">
                {totalScans.toLocaleString()} {totalScans === 1 ? "view" : "views"}
              </span>
              <button
                className="url-card-stats-btn"
                onClick={(e) => { e.stopPropagation(); openStatistics(); }}
                title="View detailed statistics"
                aria-label="Open statistics dashboard"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="20" x2="12" y2="10" />
                  <line x1="18" y1="20" x2="18" y2="4" />
                  <line x1="6" y1="20" x2="6" y2="16" />
                </svg>
              </button>
            </>
          )}
        </div>
      </div>

      <div className="card-actions">
        <div className="card-download-wrapper" ref={downloadRef}>
          <Button
            className="card-btn"
            onClick={(e) => { e.stopPropagation(); setDownloadMenuOpen((o) => !o); }}
            variant="secondary"
            type="button"
          >
            Download
            <span className="url-card-download-icon"><DownloadIcon /></span>
          </Button>
          {downloadMenuOpen && (
            <div className="card-download-dropdown">
              <button className="card-download-option" onClick={() => handleDownloadSelect("png")}>PNG</button>
              <button className="card-download-option" onClick={() => handleDownloadSelect("svg")}>SVG</button>
            </div>
          )}
        </div>

        <Button className="card-btn" onClick={() => onCopy(shortUrl)} variant="primary" type="button">
          Copy URL
        </Button>
      </div>
    </>
  );

  if (!showHeader) {
    return (
      <div className="base-card-content">
        <div className="base-card-qr">
          <div className="card-qr-image">
            {renderQRCode()}
          </div>
          {/* Hidden static QR used as the download capture target — fixed off-screen so layout is never affected */}
          <div style={{ position: "fixed", left: "-9999px", top: "-9999px" }}>
            <div
              className="card-qr-image"
              id={`qr-code-${shortCode}`}
            >
              <QRCodeRenderer
                value={shortUrl}
                size={QR_CONFIG.DISPLAY_SIZE}
                color={customColor}
                withLogo={shouldShowLogo}
                logoUrl={shouldShowLogo ? logoUrl : undefined}
              />
            </div>
          </div>
        </div>
        <div className="base-card-info">
          {renderInfoSection()}
        </div>
      </div>
    );
  }

  return (
    <>
      <BaseCard
        id={shortCode}
        defaultExpanded={defaultExpanded}
        renderHeader={({ isExpanded, onToggle }) => (
          <CardHeader
            title={name}
            isExpanded={isExpanded}
            onToggle={onToggle}
            rightContent={renderFavouriteButton()}
            menuItems={menuItems}
          />
        )}
      >
        <CardContent
          qrSection={
            <div className="card-qr-image" id={`qr-code-${shortCode}`}>
              {renderQRCode()}
            </div>
          }
          infoSection={renderInfoSection()}
        />
      </BaseCard>

      {editVisible && (
        <EditURLEntryModal
          id={shortCode}
          name={editedName}
          url={editedUrl}
          expirationDate={editedExpirationDate}
          qrOptions={qrOptions}
          customColor={customColor}
          setEditedName={setEditedName}
          setEditedUrl={setEditedUrl}
          setEditedExpirationDate={setEditedExpirationDate}
          setQrOptions={setQrOptions}
          setCustomColor={setCustomColor}
          prefix={editedPrefix}
          initialPrefix={initialPrefix}
          availablePrefixes={PREFIX_OPTIONS}
          setEditedPrefix={setEditedPrefix}
          onSave={({ name: n, url: u, expirationDate: e, customColor: c, withLogo: w, selectedLogoId: s, prefix: p }) => {
            onUpdate(shortCode, editedUrl, editedName, u, n, e, c, w, p, s);
            closeEditModal();
          }}
          onCancel={closeEditModal}
          initialName={name}
          initialUrl={url}
          initialExpirationDate={expirationDate ? new Date(expirationDate).toISOString().slice(0, 10) : ""}
          initialQrOptions={initialQrOptions}
          initialCustomColor={initialCustomColor}
          allLogos={allLogos}
          onLogoDelete={handleLogoDelete}
          onLogoUpload={uploadLogo}
          validateLogoFile={validateLogoFile}
          validateLogoName={validateLogoName}
          onDelete={onDelete}
        />
      )}

      <StatisticsModal shortCode={shortCode} name={name} isOpen={isStatisticsOpen} onClose={closeStatistics} />
    </>
  );
};

export default URLEntry;